import axiosInstance from "core/api/axiosInstance";

// ==================== FUNCIONES DE UTILIDAD ====================

const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return {
      hasToken: !!token,
      token: token || null,
      tokenLength: token ? token.length : 0,
      hasUser: !!user,
      userData: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    console.error("Error obteniendo información de autenticación:", error);
    return { hasToken: false, token: null, tokenLength: 0, hasUser: false, userData: null };
  }
};

const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.000`;
};

const handleServiceError = (error, serviceName) => {
  console.error(`❌ Error en ${serviceName}:`, error);
  let errorMessage = `Error en ${serviceName}`;
  let errorDetails = {};

  if (error.response) {
    const backendMessage = error.response.data?.message || error.response.data?.msm || "";
    const backendError = error.response.data?.error || "";
    errorMessage =
      backendMessage ||
      backendError ||
      `Error ${error.response.status}: ${error.response.statusText || "Error desconocido"}`;
    errorDetails = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers,
    };

    if (error.response.status === 401)
      errorMessage =
        backendMessage || "No autorizado. Su sesión ha expirado o el token es inválido.";
    else if (error.response.status === 403)
      errorMessage =
        backendMessage || "Acceso denegado. No tiene permisos para realizar esta acción.";
    else if (error.response.status === 404) errorMessage = "Endpoint no encontrado.";
    else if (error.response.status >= 500)
      errorMessage = backendMessage || "Error interno del servidor.";
  } else if (error.request) {
    errorMessage = "No se pudo conectar con el servidor. Verifique su conexión a internet.";
    errorDetails = { request: error.request };
  } else {
    errorMessage = error.message || "Error de configuración de la solicitud";
    errorDetails = { message: error.message, stack: error.stack };
  }

  return { error: true, message: errorMessage, details: errorDetails, shouldLogout: false };
};

const validateCierreData = (cierreData) => {
  const errors = [];
  if (!cierreData.user_kme && !cierreData.user_gp)
    errors.push("Se requiere al menos un usuario (user_kme o user_gp)");
  if (typeof cierreData.efectivoInicial !== "number" || isNaN(cierreData.efectivoInicial))
    errors.push("El efectivo inicial debe ser un número válido");
  if (typeof cierreData.recaudoEfectivo !== "number" || isNaN(cierreData.recaudoEfectivo))
    errors.push("El recaudo en efectivo debe ser un número válido");
  if (cierreData.efectivoInicial < 0) errors.push("El efectivo inicial no puede ser negativo");
  if (cierreData.recaudoEfectivo < 0) errors.push("El recaudo en efectivo no puede ser negativo");
  return { isValid: errors.length === 0, errors };
};

const createDefaultCierreItem = (index) => ({
  id: String(index + 1),
  usuario: "N/A",
  supervisor: "N/A",
  fechaCierre: "N/A",
  efectivoInicial: 0,
  recaudoEfectivo: 0,
  recaudoTarjeta: 0,
  efectivoSistema: 0,
  tarjetaSistema: 0,
  totalSistema: 0,
  diferenciaEfectivo: 0,
  diferenciaTarjeta: 0,
  transaccionesEfectivo: [],
  transaccionesTarjeta: [],
  fechaCreacion: "N/A",
  fechaActualizacion: "N/A",
  deletedAt: null,
  balance: {
    FHInicio: "N/A",
    FHFin: "N/A",
    Usuario: "N/A",
    NumeroCaja: "N/A",
    NumeroTurno: "N/A",
    NumeroOperaciones: 0,
    raw: {},
  },
  fechaInicio: "N/A",
  fechaFin: "N/A",
  numeroCaja: "N/A",
  numeroTurno: "N/A",
});

// ==================== SERVICIO PARA GUARDAR PDF ====================

export const saveTurnoPDFService = async (pdfFile, idSave, options = {}) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken)
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    if (!pdfFile) throw new Error("Se requiere un archivo PDF para guardar.");
    if (!idSave) throw new Error("Se requiere el ID del registro de cierre (idSave).");
    if (!(pdfFile instanceof File)) throw new Error("El archivo debe ser una instancia de File.");

    const formData = new FormData();
    formData.append("file", pdfFile);
    formData.append("id", String(idSave));

    const config = {
      headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authInfo.token}` },
      timeout: 60000,
      onUploadProgress: options.onUploadProgress || null,
    };

    const response = await axiosInstance.post("/api/tp/save-pdf-iserra", formData, config);
    const responseData = response.data;

    if (!responseData) throw new Error("No se recibió respuesta del servidor al guardar el PDF.");

    const successStatus = response.status >= 200 && response.status < 300;
    const successMessage = responseData.msm || responseData.message || "";
    const isSuccess =
      successStatus &&
      (successMessage.toLowerCase().includes("succesful") ||
        successMessage.toLowerCase().includes("success") ||
        responseData.success === true);

    if (!isSuccess)
      throw new Error(`Error del servidor: ${successMessage || "No se pudo guardar el PDF"}`);

    return {
      success: true,
      data: responseData,
      message: successMessage || "PDF guardado exitosamente",
      idSave: idSave,
      fileName: pdfFile.name,
      fileSize: pdfFile.size,
      timestamp: new Date().toISOString(),
      responseStatus: response.status,
    };
  } catch (error) {
    console.error("❌ Error en saveTurnoPDFService:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      idSave: idSave,
      fileName: pdfFile?.name,
    });
    let errorMessage = error.message || "Error al guardar el PDF";
    let errorType = "GENERAL";
    if (error.message?.includes("Network Error") || error.message?.includes("timeout")) {
      errorMessage = "Error de conexión con el servidor. Verifique su internet.";
      errorType = "NETWORK";
    } else if (error.response?.status === 401) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Error de autenticación";
      errorType = "AUTH";
    } else if (error.response?.status === 403) {
      errorMessage =
        error.response.data?.message ||
        error.response.data?.msm ||
        error.response.data?.error ||
        "Acceso denegado";
      errorType = "FORBIDDEN";
    } else if (error.response?.status === 404) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Endpoint no encontrado";
      errorType = "ENDPOINT_NOT_FOUND";
    } else if (error.response?.status === 413) {
      errorMessage = "El archivo PDF es demasiado grande. Intente con un archivo más pequeño.";
      errorType = "FILE_TOO_LARGE";
    } else if (error.response?.status >= 500) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Error interno del servidor";
      errorType = "SERVER_ERROR";
    }
    if (error.response?.data) {
      const backendError =
        error.response.data.message || error.response.data.msm || error.response.data.error || "";
      if (backendError) errorMessage = backendError;
    }
    return {
      success: false,
      error: errorMessage,
      errorType: errorType,
      details: {
        originalError: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        idSave: idSave,
        fileName: pdfFile?.name,
        timestamp: new Date().toISOString(),
        backendMessage: error.response?.data?.message,
        backendMsm: error.response?.data?.msm,
        backendError: error.response?.data?.error,
      },
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA DESCARGAR PDF ====================

export const downloadTurnoPDFService = async (id, options = {}) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken)
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    if (!id) throw new Error("Se requiere el ID del registro de cierre para descargar el PDF.");

    const config = {
      headers: { Authorization: `Bearer ${authInfo.token}`, Accept: "application/pdf" },
      responseType: "blob",
      timeout: 60000,
      onDownloadProgress: options.onDownloadProgress || null,
    };

    const response = await axiosInstance.get(`/api/tp/pdf/${id}`, config);
    if (!response.data) throw new Error("No se recibió datos del servidor al descargar el PDF.");
    if (response.status !== 200)
      throw new Error(
        `Error ${response.status}: ${response.statusText || "Error al descargar el PDF"}`
      );

    const contentType = response.headers["content-type"];
    if (!contentType || !contentType.includes("pdf"))
      console.warn("⚠️ La respuesta no es un PDF:", contentType);

    let fileName = options.fileName || `cierre-turno-${id}.pdf`;
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (fileNameMatch && fileNameMatch[1]) fileName = fileNameMatch[1].replace(/['"]/g, "");
    }

    const blob = new Blob([response.data], { type: "application/pdf" });
    if (blob.size === 0) throw new Error("El archivo PDF descargado está vacío.");

    return {
      success: true,
      data: blob,
      fileName: fileName,
      id: id,
      size: blob.size,
      contentType: contentType,
      timestamp: new Date().toISOString(),
      responseStatus: response.status,
    };
  } catch (error) {
    console.error("❌ Error en downloadTurnoPDFService:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      id: id,
    });
    let errorMessage = error.message || "Error al descargar el PDF";
    let errorType = "GENERAL";
    if (error.message?.includes("Network Error") || error.message?.includes("timeout")) {
      errorMessage = "Error de conexión con el servidor. Verifique su internet.";
      errorType = "NETWORK";
    } else if (error.response?.status === 401) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Error de autenticación";
      errorType = "AUTH";
    } else if (error.response?.status === 403) {
      errorMessage =
        error.response.data?.message ||
        error.response.data?.msm ||
        error.response.data?.error ||
        "Acceso denegado";
      errorType = "FORBIDDEN";
    } else if (error.response?.status === 404) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "PDF no encontrado";
      errorType = "PDF_NOT_FOUND";
    } else if (error.response?.status === 413) {
      errorMessage = "El archivo PDF es demasiado grande para descargar.";
      errorType = "FILE_TOO_LARGE";
    } else if (error.response?.status >= 500) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Error interno del servidor";
      errorType = "SERVER_ERROR";
    }
    if (error.response?.data) {
      try {
        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorData.msm || errorData.error || errorMessage;
        }
      } catch (e) {}
    }
    return {
      success: false,
      error: errorMessage,
      errorType: errorType,
      details: {
        originalError: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        id: id,
        timestamp: new Date().toISOString(),
      },
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

export const downloadAndSavePDF = async (id, options = {}) => {
  try {
    const result = await downloadTurnoPDFService(id, options);
    if (!result.success) throw new Error(result.error);
    const blobUrl = window.URL.createObjectURL(result.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = options.fileName || result.fileName || `cierre-turno-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
    return true;
  } catch (error) {
    console.error("❌ Error en downloadAndSavePDF:", error);
    throw error;
  }
};

// ==================== SERVICIOS PRINCIPALES ====================

export const getUsersForCierreTurnoService = async () => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken)
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    const response = await axiosInstance.get("/api/tp/get-user-kme");
    if (!response || !response.data) throw new Error("No se recibieron datos del servidor");
    const responseData = response.data;
    let usersData = [];
    if (responseData.data) {
      if (Array.isArray(responseData.data)) usersData = responseData.data;
      else if (responseData.data.data && Array.isArray(responseData.data.data))
        usersData = responseData.data.data;
    } else if (Array.isArray(responseData)) usersData = responseData;
    else {
      const possibleUserFields = ["users", "usuarios", "Usuarios", "userList"];
      for (const field of possibleUserFields) {
        if (responseData[field] && Array.isArray(responseData[field])) {
          usersData = responseData[field];
          break;
        }
      }
    }
    if (usersData.length === 0 && responseData && typeof responseData === "object") {
      if (typeof responseData === "object" && !Array.isArray(responseData)) {
        usersData = Object.values(responseData).filter(
          (item) =>
            item && typeof item === "object" && (item.Usuario || item.usuario || item.username)
        );
      }
    }
    return {
      success: true,
      data: usersData,
      rawResponse: responseData,
      message: "Usuarios obtenidos exitosamente",
    };
  } catch (error) {
    const errorResult = handleServiceError(error, "getUsersForCierreTurnoService");
    return { success: false, error: errorResult.message, details: errorResult.details, data: [] };
  }
};

/**
 * Obtiene la lista de turnos de un usuario específico
 * CORREGIDO - Devuelve la fecha UTC original sin conversión
 */
export const getUserTurnsService = async (username) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken)
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    if (!username || username.trim() === "") throw new Error("Se requiere un nombre de usuario");

    const payload = { user_kme: username.trim() };

    const response = await axiosInstance.post("/api/tp/get-turn", payload);
    const responseData = response.data;
    if (!responseData) throw new Error("No se recibió respuesta del servidor");

    let turns = [];
    if (responseData.data && Array.isArray(responseData.data)) turns = responseData.data;
    else if (Array.isArray(responseData)) turns = responseData;

    /**
     * Parsea una fecha UTC y devuelve la hora UTC original (sin conversión a local)
     */
    const parseUTCDate = (utcDateString) => {
      if (!utcDateString) return { fecha: "N/A", hora: "N/A", completa: "N/A" };
      try {
        const date = new Date(utcDateString);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        let hour = date.getUTCHours();
        const minute = String(date.getUTCMinutes()).padStart(2, "0");
        const second = String(date.getUTCSeconds()).padStart(2, "0");

        const ampm = hour >= 12 ? "p.m." : "a.m.";
        const hour12 = hour % 12 || 12;
        const fecha = `${day}/${month}/${year}`;
        const hora = `${hour12}:${minute}:${second} ${ampm}`;

        return { fecha, hora, completa: `${fecha} ${hora}` };
      } catch (error) {
        console.error("Error parseando fecha:", error);
        return { fecha: "N/A", hora: "N/A", completa: "N/A" };
      }
    };

    const formattedTurns = turns.map((turn) => {
      const inicio = parseUTCDate(turn.FHInicio);
      const fin = parseUTCDate(turn.FHFin);

      return {
        id: turn.NumeroLinea,
        numeroLinea: turn.NumeroLinea,
        numeroCaja: turn.NumeroCaja,
        numeroTurno: turn.NumeroTurno,
        usuario: turn.Usuario,
        fhInicio: turn.FHInicio,
        fhFin: turn.FHFin,
        inicioFormateado: inicio.completa,
        finFormateado: fin.completa,
        displayText: `Turno #${turn.NumeroTurno} | Caja ${turn.NumeroCaja} | ${inicio.fecha}`,
        detailText: `Inicio: ${inicio.completa} | Fin: ${fin.completa}`,
        numeroOperaciones: turn.NumeroOperaciones || 0,
        rawData: turn,
      };
    });

    return {
      success: true,
      data: formattedTurns,
      rawResponse: responseData,
      message: "Turnos obtenidos exitosamente",
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("❌ Error en getUserTurnsService:", error);
    let errorMessage = error.message || "Error al obtener los turnos";
    if (error.response?.data)
      errorMessage = error.response.data.msm || error.response.data.message || errorMessage;
    return { success: false, error: errorMessage, data: [] };
  }
};

// ==================== SERVICIO PARA CERRAR TURNO ====================

export const closeTurnoService = async (cierreData) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken)
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    const validation = validateCierreData(cierreData);
    if (!validation.isValid) throw new Error(`Datos inválidos: ${validation.errors.join(", ")}`);

    let isAdminUser = false;
    try {
      const userData = localStorage.getItem("user");
      if (userData) {
        const parsedUser = JSON.parse(userData);
        let roleStr = "";
        let roleObject = null;
        if (parsedUser.user?.role) roleObject = parsedUser.user.role;
        else if (parsedUser.role) roleObject = parsedUser.role;
        else if (parsedUser.more?.role) roleObject = parsedUser.more.role;

        if (roleObject) {
          if (typeof roleObject === "object" && roleObject !== null)
            roleStr = (roleObject.name || roleObject.nombre || roleObject.roleName || "")
              .toString()
              .toLowerCase();
          else if (typeof roleObject === "string") roleStr = roleObject.toLowerCase();
          else if (typeof roleObject === "number") roleStr = String(roleObject);
        }
        const username = parsedUser.user?.username || parsedUser.username || "";
        isAdminUser =
          roleStr.includes("admin iserra100") ||
          roleStr.includes("administrador") ||
          username.toLowerCase() === "admin" ||
          roleStr.includes("admin") ||
          roleStr.includes("gerencia") ||
          roleStr.includes("supervisor") ||
          (parsedUser.user?.id && [10, 14, 15].includes(parsedUser.user.id)) ||
          (parsedUser.id && [10, 14, 15].includes(parsedUser.id));
      }
    } catch (roleError) {
      console.error("❌ Error analizando rol del usuario:", roleError);
    }

    const userToSend = cierreData.user_kme || cierreData.user_gp || "Explotador";
    const now = new Date();
    const fechaCierreFormatted = formatDateForAPI(now);

    const payload = {
      user_kme: userToSend,
      user_gp: userToSend,
      metadata: {
        efectivoInicial: Number(cierreData.efectivoInicial) || 0,
        recaudoEfectivo: Number(cierreData.recaudoEfectivo) || 0,
        fechaCierre: fechaCierreFormatted,
      },
      more: { turnoInfo: { usuario: userToSend, fechaCierre: fechaCierreFormatted } },
      date_close: fechaCierreFormatted,
    };

    if (isAdminUser && cierreData.numeroLinea) {
      payload.lineNumber = cierreData.numeroLinea;
      payload.metadata.numeroLinea = cierreData.numeroLinea;
    } else if (cierreData.numeroLinea && !isAdminUser);
    else if (!isAdminUser);

    const response = await axiosInstance.post("/api/tp/set-close", payload);
    const responseData = response.data;
    if (!responseData) throw new Error("No se recibió respuesta del servidor");

    let balanceData = null;
    let hasBalance = false;
    if (responseData.balance && typeof responseData.balance === "object") {
      if (Array.isArray(responseData.balance) && responseData.balance.length > 0) {
        balanceData = responseData.balance[0];
        hasBalance = true;
      } else if (responseData.balance.NumeroTurno) {
        balanceData = responseData.balance;
        hasBalance = true;
      }
    } else if (responseData.data?.balance) {
      if (Array.isArray(responseData.data.balance) && responseData.data.balance.length > 0) {
        balanceData = responseData.data.balance[0];
        hasBalance = true;
      } else if (
        typeof responseData.data.balance === "object" &&
        responseData.data.balance.NumeroTurno
      ) {
        balanceData = responseData.data.balance;
        hasBalance = true;
      }
    }

    const responseMessage = responseData.message || responseData.msm || "";
    const responseSuccess =
      responseData.success !== false && response.status >= 200 && response.status < 300;

    if (responseMessage) {
      const lowerMessage = responseMessage.toLowerCase();
      if (
        lowerMessage.includes("turno") ||
        lowerMessage.includes("balance") ||
        lowerMessage.includes("no encontrado") ||
        lowerMessage.includes("no hay") ||
        lowerMessage.includes("no se encontró") ||
        lowerMessage.includes("sin turno") ||
        lowerMessage.includes("no existe")
      )
        throw new Error(`TURNO_NO_ENCONTRADO: ${responseMessage}`);
    }

    if (!hasBalance && !responseSuccess)
      throw new Error(responseMessage || "No se pudo cerrar el turno");

    const transData = responseData.data?.trans || responseData.trans || {};
    const efectivoInicial = Number(cierreData.efectivoInicial) || 0;
    const recaudoEfectivo = Number(cierreData.recaudoEfectivo) || 0;
    const efectivoSistema = Number(transData.valorE) || 0;
    const tarjetaSistema = Number(transData.valorT) || 0;
    const diferenciaEfectivo = recaudoEfectivo - efectivoSistema;
    const idSave = responseData.data?.idSave || responseData.idSave || null;
    const balanceArray = hasBalance ? [balanceData] : [];

    const resultData = {
      ...responseData.data,
      diferenciaEfectivo,
      diferenciaTotal: diferenciaEfectivo,
      efectivoInicial,
      recaudoEfectivo,
      efectivoSistema,
      tarjetaSistema,
      balance: balanceArray,
      trans: transData,
      fechaCierre: fechaCierreFormatted,
      user_kme: userToSend,
      user_gp: userToSend,
      backendSuccess: responseData.success,
      numeroCaja: balanceData?.NumeroCaja,
      numeroTurno: balanceData?.NumeroTurno,
      inicioTurno: balanceData?.FHInicio,
      finTurno: balanceData?.FHFin,
      usuarioSistema: balanceData?.Usuario || userToSend,
      idSave: idSave,
      numeroLineaEnviado: isAdminUser && cierreData.numeroLinea ? cierreData.numeroLinea : null,
      fueAdmin: isAdminUser,
    };

    const finalSuccess = hasBalance ? true : responseSuccess;
    return {
      success: finalSuccess,
      data: resultData,
      rawResponse: responseData,
      message:
        responseData.msm ||
        (hasBalance ? "Turno cerrado exitosamente" : "No se encontró turno activo"),
      timestamp: new Date().toISOString(),
      hasBalance,
      responseStatus: response.status,
      idSave: idSave,
      numeroLineaEnviado: isAdminUser && cierreData.numeroLinea ? cierreData.numeroLinea : null,
      fueAdmin: isAdminUser,
    };
  } catch (error) {
    console.error("❌ Error en closeTurnoService:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      usuario: cierreData?.user_kme,
      numeroLinea: cierreData?.numeroLinea,
    });
    let errorMessage = error.message || "Error al cerrar el turno";
    let errorType = "GENERAL";
    if (error.message?.includes("Network Error") || error.message?.includes("timeout")) {
      errorMessage = "Error de conexión con el servidor. Verifique su internet.";
      errorType = "NETWORK";
    } else if (error.response?.status === 401) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Error de autenticación";
      errorType = "AUTH";
    } else if (error.response?.status === 403) {
      errorMessage =
        error.response.data?.message ||
        error.response.data?.msm ||
        error.response.data?.error ||
        "Acceso denegado";
      errorType = "FORBIDDEN";
    } else if (error.response?.status === 404) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Endpoint no encontrado";
      errorType = "ENDPOINT_NOT_FOUND";
    } else if (error.response?.status >= 500) {
      errorMessage =
        error.response.data?.message || error.response.data?.msm || "Error interno del servidor";
      errorType = "SERVER_ERROR";
    }
    if (error.message?.includes("TURNO_NO_ENCONTRADO")) {
      errorType = "TURNO_NO_ENCONTRADO";
      errorMessage = error.message.replace("TURNO_NO_ENCONTRADO: ", "");
    }
    if (error.response?.data) {
      const backendError =
        error.response.data.message || error.response.data.msm || error.response.data.error || "";
      if (backendError) errorMessage = backendError;
    }
    return {
      success: false,
      error: errorMessage,
      errorType: errorType,
      details: {
        originalError: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        usuarioIntentado: cierreData?.user_kme,
        numeroLineaIntentado: cierreData?.numeroLinea,
        timestamp: new Date().toISOString(),
        backendMessage: error.response?.data?.message,
        backendMsm: error.response?.data?.msm,
        backendError: error.response?.data?.error,
      },
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA OBTENER DATOS DE CIERRE ====================

export const getCierreTurnoDataService = async (filters = {}) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken)
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");

    let url = "/api/tp/get-data";
    const queryParams = [];
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      if (!isNaN(startDate.getTime()))
        queryParams.push(`startDate=${encodeURIComponent(filters.startDate)}`);
    }
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      if (!isNaN(endDate.getTime()))
        queryParams.push(`endDate=${encodeURIComponent(filters.endDate)}`);
    }
    if (filters.usuario) queryParams.push(`usuario=${encodeURIComponent(filters.usuario.trim())}`);
    if (filters.supervisor)
      queryParams.push(`supervisor=${encodeURIComponent(filters.supervisor.trim())}`);
    if (filters.search) queryParams.push(`search=${encodeURIComponent(filters.search.trim())}`);
    if (filters.page) queryParams.push(`page=${encodeURIComponent(filters.page)}`);
    if (filters.limit) queryParams.push(`limit=${encodeURIComponent(filters.limit)}`);
    if (queryParams.length > 0) url += `?${queryParams.join("&")}`;

    const response = await axiosInstance.get(url);
    const { data } = response;

    let dataArray = [];
    if (data && data.data && Array.isArray(data.data)) dataArray = data.data;
    else if (Array.isArray(data)) dataArray = data;
    else if (data && typeof data === "object") {
      if (data.result && Array.isArray(data.result)) dataArray = data.result;
      else if (data.records && Array.isArray(data.records)) dataArray = data.records;
      else if (data.items && Array.isArray(data.items)) dataArray = data.items;
    }

    const formattedData = dataArray.map((item, index) => {
      try {
        if (!item || typeof item !== "object") return createDefaultCierreItem(index);
        const metadata = item.metadata || {};
        const more = item.more || {};
        const balance = metadata.balance || more.balance || {};
        const trans = metadata.trans || more.trans || {};
        const transE = trans.trasnE || [];

        const efectivoInicial = Number(metadata.efectivoInicial || more.efectivoInicial || 0);
        const recaudoEfectivo = Number(metadata.recaudoEfectivo || more.recaudoEfectivo || 0);
        const valorE = Number(trans.valorE || 0);
        const diferenciaEfectivo = recaudoEfectivo - valorE;
        const usuario =
          item.user_kme || metadata.user_kme || more.user_kme || balance.Usuario || "Explotador";
        const supervisor = item.user_gp || metadata.user_gp || more.user_gp || "Explotador";

        return {
          id: String(item.id || index + 1),
          user_kme: usuario,
          user_gp: supervisor,
          usuario: usuario,
          supervisor: supervisor,
          fechaCierre: metadata.fechaCierre || more.fechaCierre || item.date_close || "N/A",
          efectivoInicial: efectivoInicial,
          recaudoEfectivo: recaudoEfectivo,
          efectivoSistema: valorE,
          diferenciaEfectivo: diferenciaEfectivo,
          diferenciaTotal: diferenciaEfectivo,
          transaccionesEfectivo: transE.map((transItem, transIndex) => ({
            id: `${index}-${transIndex}`,
            caja: String(transItem.caja || "N/A"),
            tipo: String(transItem.tipo || "N/A"),
            Importe: Number(transItem.Importe) || 0,
            Importe_MedioPago: Number(transItem.Importe_MedioPago) || 0,
          })),
          fechaCreacion: item.createdAt || "N/A",
          fechaActualizacion: item.updatedAt || "N/A",
          deletedAt: item.deletedAt || null,
          date_close: item.date_close || "N/A",
          balance: {
            FHInicio: balance.FHInicio || "N/A",
            FHFin: balance.FHFin || "N/A",
            Usuario: balance.Usuario || usuario,
            NumeroCaja: balance.NumeroCaja || "N/A",
            NumeroTurno: balance.NumeroTurno || "N/A",
            NumeroOperaciones: Number(balance.NumeroOperaciones) || 0,
            Impuesto: balance.Impuesto || 0,
            PrimeraOperacion: balance.PrimeraOperacion || 0,
            UltimaOperacion: balance.UltimaOperacion || 0,
          },
          metadata: metadata,
          more: more,
          fechaInicio: balance.FHInicio || "N/A",
          fechaFin: balance.FHFin || "N/A",
          numeroCaja: balance.NumeroCaja || "N/A",
          numeroTurno: balance.NumeroTurno || "N/A",
          rawData: item,
        };
      } catch (itemError) {
        console.error(`❌ Error procesando item ${index}:`, itemError);
        return createDefaultCierreItem(index);
      }
    });

    return {
      success: true,
      data: formattedData,
      total: formattedData.length,
      rawResponse: data,
      message: "Datos de cierre de turno obtenidos exitosamente",
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("❌ Error en getCierreTurnoDataService:", error);
    const errorResult = handleServiceError(error, "getCierreTurnoDataService");
    return {
      success: false,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      data: [],
      total: 0,
      timestamp: new Date().toISOString(),
    };
  }
};

export default {
  getUsersForCierreTurnoService,
  closeTurnoService,
  getCierreTurnoDataService,
  saveTurnoPDFService,
  downloadTurnoPDFService,
  downloadAndSavePDF,
  getUserTurnsService,
};
